import { Injectable, inject } from '@angular/core';
import {
  SecurityRole,
  SessionDto,
  SessionService,
  TenantDto,
  TenantLoginAdminRequestDto,
  UserDto,
  UserFeatureFlag,
} from 'src/app/api/client';
import {
  concat,
  defer,
  EMPTY,
  firstValueFrom,
  from,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import {
  catchError,
  finalize,
  ignoreElements,
  shareReplay,
} from 'rxjs/operators';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
} from '@angular/common/http';
import { ApiConfigurationService } from 'src/app/api/api-configuration.service';
import { TenantService } from '../tenant/tenant.service';
import {
  isTenantAccessDenied,
  TenantAccessLostError,
} from '../tenant/tenant-access-lost.error';
import { TenantAccessService } from '../tenant/tenant-access.service';
import { DeviceService } from '../device/device.service';
import { map } from 'rxjs/operators';
import { ChatService } from 'src/app/ai-chat/chat.service';
import {
  findGeneratedTestSession,
  syncGeneratedTestSession,
} from 'src/app/dev-tools/dev-test-data';

/**
 * Thrown by {@link UserService.refreshSession} when there is no refresh token to
 * refresh with. Deliberately carries no `status`, so code that only branches on
 * `status === 401` (AuthInterceptor) is unaffected.
 */
export class MissingRefreshTokenError extends Error {
  constructor() {
    super('Missing refresh token');
    this.name = 'MissingRefreshTokenError';
  }
}

/**
 * True when a failed refresh/tenant-login means the client's login is no
 * longer usable and cannot be recovered without a fresh login: there is no
 * refresh token at all, or the backend answered 401 (refresh token
 * invalid/revoked). Lost organization access is a 403 and surfaces as
 * TenantAccessLostError, not here. Treated like AuthInterceptor treats a 401
 * on retry.
 */
export function isSessionExpiredError(error: unknown): boolean {
  return (
    (error instanceof HttpErrorResponse && error.status === 401) ||
    error instanceof MissingRefreshTokenError
  );
}

/**
 * Marks a session that was entered via the test UI user switch from a super
 * admin session (directly or through earlier switches). Lets the super admin
 * keep using the test UI outside the local dev environment (#1047).
 */
const SWITCHED_FROM_SUPER_ADMIN_KEY = 'session.switchedFromSuperAdmin';

/** A shared session refresh and the tenant selection it was started for (null: tenant-less). */
interface InFlightRefresh {
  tenantId: string | null;
  session$: Observable<SessionDto>;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly sessionService = inject(SessionService);
  private readonly httpClient = inject(HttpClient);
  private readonly tenantService = inject(TenantService);
  private readonly tenantAccessService = inject(TenantAccessService);
  private readonly apiConfigurationService = inject(ApiConfigurationService);
  private readonly deviceService = inject(DeviceService);
  private refreshInFlight?: InFlightRefresh;

  /**
   * Check whether a user is logged in.
   * @returns true, if a user is signed in.
   */
  public async isAuthenticated(): Promise<boolean> {
    return !!this.getJwtToken();
  }

  public isSuperAdmin(): boolean {
    return localStorage.getItem('session.isSuperAdmin') === 'true';
  }

  /**
   * True while the active session was switched to via the test UI from a super
   * admin session. Never true without an active session. Client-side
   * convenience only, like {@link isSuperAdmin}: it only decides whether the
   * test UI is shown, the TestData endpoints are secured on the server.
   */
  public isSwitchedFromSuperAdmin(): boolean {
    return (
      !!this.getJwtToken() &&
      localStorage.getItem(SWITCHED_FROM_SUPER_ADMIN_KEY) === 'true'
    );
  }

  public hasRole(role: SecurityRole): boolean {
    const user = this.getUserFromSession();
    return (
      user.roles.includes(role) ||
      user.roles.includes(SecurityRole.Administrator) ||
      (this.isSuperAdmin() && !this.isTenantLoginAdmin())
    );
  }

  /** Whether the signed-in user carries the given feature flag in the stored session. */
  public hasFeatureFlag(flag: UserFeatureFlag): boolean {
    return this.getUserFromSession()?.featureFlags?.includes(flag) ?? false;
  }

  public invalidateSession() {
    localStorage.removeItem('session.user');
    localStorage.removeItem('session.token');
    localStorage.removeItem('session.refresh-token');
    localStorage.removeItem('session.tenants');
    localStorage.removeItem('session.isSuperAdmin');
    localStorage.removeItem('session.isTenantLoginAdmin');
    // A leftover flag would permanently disable the dev-tools snapshot sync.
    localStorage.removeItem('session.impersonatedBy');
    localStorage.removeItem(SWITCHED_FROM_SUPER_ADMIN_KEY);
    this.tenantService.clearTenantOnLogout();
    ChatService.clearStorage();

    // GDPR: Deactivated - do not send user data to Sentry
    // Clear user context in Sentry
    // this.errorLoggingService.clearUserContext();
  }

  public async logout(): Promise<void> {
    try {
      await firstValueFrom(this.sessionService.apiV2SessionLogoutPost());
    } catch {
      // Server-side logout failed — still clear client state
    }
    this.invalidateSession();
  }

  /**
   * Replaces the active session with the given one (e.g. a generated test-user
   * session) and reloads the app so all in-memory caches are dropped.
   *
   * Used by the dev-tools test-data generator and the in-app test-mode panel.
   * A switch made from a super admin session (or from a session that was
   * itself switched to from one) is marked, see {@link isSwitchedFromSuperAdmin}.
   */
  public switchToSession(requestedSession: SessionDto): void {
    // The caller may hold a stale copy (modal/page opened before a token
    // rotation); prefer the snapshot that writeSessionData keeps in sync with
    // the live session so we never restore an already-rotated refresh token.
    const session =
      findGeneratedTestSession(requestedSession.user.id) ?? requestedSession;
    // Read before invalidateSession, which drops session.isSuperAdmin and
    // session.switchedFromSuperAdmin.
    const fromSuperAdmin =
      this.isSuperAdmin() || this.isSwitchedFromSuperAdmin();
    this.invalidateSession();
    this.writeSessionData(session);
    if (fromSuperAdmin) {
      localStorage.setItem(SWITCHED_FROM_SUPER_ADMIN_KEY, 'true');
    }

    if (session.tenants?.length && session.tenants[0]) {
      this.tenantService.setSelectedTenant(session.tenants[0].id);
    }

    // Redirect (not NavController) to clear all caches for a clean session.
    this.redirectToRoot();
  }

  /** Extracted so unit tests can spy on it instead of navigating the test page. */
  private redirectToRoot(): void {
    window.location.href = '/';
  }

  /**
   * Stores the session of a fresh login. Unlike {@link writeSessionData}
   * (also used for token refreshes) it never carries over state of a previous
   * session, such as a test UI switch from a super admin session.
   */
  public writeLoginSession(session: SessionDto): void {
    localStorage.removeItem(SWITCHED_FROM_SUPER_ADMIN_KEY);
    this.writeSessionData(session);
  }

  public writeSessionData(session: SessionDto) {
    // Keep the dev-tools test-user snapshot in step with every token rotation.
    // Not while impersonating or in tenant-login-admin mode: those sessions
    // belong to a different identity/scope and must never overwrite the
    // generated user's own snapshot.
    if (!this.isImpersonating() && !session.isTenantLoginAdmin) {
      syncGeneratedTestSession(session);
    }
    localStorage.setItem('session.user', JSON.stringify(session.user));
    localStorage.setItem('session.token', session.token);
    localStorage.setItem('session.refresh-token', session.refreshToken);
    localStorage.setItem('session.tenants', JSON.stringify(session.tenants));
    localStorage.setItem(
      'session.isSuperAdmin',
      JSON.stringify(session.isSuperAdmin)
    );
    localStorage.setItem(
      'session.isTenantLoginAdmin',
      JSON.stringify(session.isTenantLoginAdmin ?? false)
    );

    // GDPR: Deactivated - do not send personal identifiable information (user ID, email, username) to Sentry
    // This would require explicit user consent and a data processing agreement with Sentry
    // Set user context in Sentry for error tracking
    // this.errorLoggingService.setUserContext(
    //   session.user.id.toString(),
    //   session.user.email,
    //   session.user.username
    // );
  }

  /**
   * Re-issues the session tokens (tenant-scoped when an organization is selected).
   *
   * Error contract - callers must not guess from the status code:
   * - 401 (HttpErrorResponse): the refresh session is dead -> the caller logs the user out.
   * - `TenantAccessLostError`: tenant-login answered 403, the user is no longer a member
   *   of the selected organization. The session is still valid; the selection is already
   *   cleared and the user informed and sent to the org selection - do NOT log out. (If the
   *   user meanwhile selected another organization, that selection is kept and re-scoped by
   *   the refresh queued for it; nothing is cleared or shown.)
   */
  public refreshSession(handler?: HttpHandler): Observable<SessionDto> {
    const tenantId = this.tenantService.getSelectedTenantId() || null;
    const running = this.refreshInFlight;
    if (running?.tenantId === tenantId) {
      // Same selection: join instead of sending the refresh token twice (the
      // backend's reuse detection would revoke the session).
      return running.session$;
    }
    // A refresh started for another selection (e.g. a tenant-less 401 refresh while the
    // user selects a tenant) must not be reused: its token would not match the selection.
    // See knowledge hub (frontend.md) "Tenant selection and token re-scope are one step".
    return this.startRefresh(tenantId, running?.session$, handler);
  }

  /**
   * Registers a shared refresh for the given selection. With a `previous$` refresh still
   * running, the new one is queued behind it: it needs the refresh token that one rotates,
   * and two requests with the same refresh token would trip the backend's reuse detection.
   *
   * `tenantId` only decides who may join. The request itself uses the selection current at
   * its start, so the newest registration - which always runs last - wins.
   */
  private startRefresh(
    tenantId: string | null,
    previous$: Observable<SessionDto> | undefined,
    handler?: HttpHandler
  ): Observable<SessionDto> {
    const refresh: InFlightRefresh = { tenantId, session$: EMPTY };
    const request$ = defer(() => this.requestSession(refresh, handler));
    const afterPrevious$ = previous$
      ? concat(this.settled(previous$), request$)
      : request$;

    refresh.session$ = afterPrevious$.pipe(
      finalize(() => this.releaseRefresh(refresh)),
      shareReplay(1)
    );
    this.refreshInFlight = refresh;
    return refresh.session$;
  }

  /**
   * Completes once the previous refresh settled; its outcome belongs to its own callers.
   * Only a dead session is passed on - re-sending a dead refresh token cannot succeed.
   */
  private settled(previous$: Observable<SessionDto>): Observable<never> {
    return previous$.pipe(
      ignoreElements(),
      catchError((error) =>
        isSessionExpiredError(error) ? throwError(() => error) : EMPTY
      )
    );
  }

  /** Unregisters the refresh unless a newer one already replaced it (see recoverFromLostTenantAccess). */
  private releaseRefresh(refresh: InFlightRefresh): void {
    if (this.refreshInFlight === refresh) {
      this.refreshInFlight = undefined;
    }
  }

  /** Reads tokens and selection only on subscription, i.e. after a queued predecessor rotated them. */
  private requestSession(
    refresh: InFlightRefresh,
    handler?: HttpHandler
  ): Observable<SessionDto> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new MissingRefreshTokenError());
    }

    const httpClient = handler ? new HttpClient(handler) : this.httpClient;
    const headers = this.buildRefreshHeaders(refreshToken);
    const selectedTenantId = this.tenantService.getSelectedTenantId();

    return this.postRefreshRequest(httpClient, headers, selectedTenantId).pipe(
      tap((result) => {
        this.writeSessionData(result);
      }),
      catchError((error) =>
        selectedTenantId && isTenantAccessDenied(error)
          ? from(
              this.recoverFromLostTenantAccess(
                refresh,
                selectedTenantId,
                httpClient,
                headers,
                error
              )
            )
          : throwError(() => error)
      )
    );
  }

  /** tenant-login when an organization is selected, else the tenant-less refresh. */
  private postRefreshRequest(
    httpClient: HttpClient,
    headers: Record<string, string>,
    selectedTenantId: string | null
  ): Observable<SessionDto> {
    return selectedTenantId
      ? this.postSessionRequest(
          httpClient,
          '/api/v1/session/tenant-login',
          { tenantId: selectedTenantId },
          headers
        )
      : this.postSessionRequest(
          httpClient,
          '/api/v1/session/refresh',
          null,
          headers
        );
  }

  private buildRefreshHeaders(refreshToken: string): Record<string, string> {
    return {
      Authorization: `Bearer ${refreshToken}`,
      ...this.deviceService.getDeviceHeaders(),
    };
  }

  private postSessionRequest(
    httpClient: HttpClient,
    path: string,
    body: object | null,
    headers: Record<string, string>
  ): Observable<SessionDto> {
    return httpClient.post<SessionDto>(
      this.apiConfigurationService.getBasePath() + path,
      body,
      { headers }
    );
  }

  private async recoverFromLostTenantAccess(
    refresh: InFlightRefresh,
    lostTenantId: string,
    httpClient: HttpClient,
    headers: Record<string, string>,
    cause: unknown
  ): Promise<never> {
    const currentTenantId = this.tenantService.getSelectedTenantId();
    if (currentTenantId && currentTenantId !== lostTenantId) {
      // The user picked another organization while this refresh ran. The refresh queued for
      // that selection re-scopes the session (tenant-login 403 did not rotate the refresh
      // token), so there is no selection to clear and nobody to send to the org selection.
      throw new TenantAccessLostError({ cause });
    }
    this.tenantService.clearSelectedTenantId();
    // The refresh now stands for the cleared selection: callers arriving during the org-list
    // refresh (their selection is now tenant-less too) must join this failing refresh, because
    // a second POST /session/refresh with the same refresh token would trip the backend's
    // reuse detection and revoke the session.
    refresh.tenantId = null;
    await this.refreshTenantListAfterAccessLoss(httpClient, headers);
    // Release only now, right before notifying. Releasing before the redirect makes anything
    // it triggers (e.g. the org selection auto-entering its only tenant) start a fresh refresh
    // instead of joining this failed one, because finalize() only runs after the error
    // reached the subscribers.
    this.releaseRefresh(refresh);
    await this.tenantAccessService.handleAccessLost();
    throw new TenantAccessLostError({ cause });
  }

  /**
   * Swaps the tenant-scoped session for a tenant-less one so the org selection lists the
   * memberships the backend knows now, not the stale ones from the last login. Best effort
   * on purpose: the user is already being redirected, and a dead refresh session (401)
   * surfaces on the next API call.
   */
  private async refreshTenantListAfterAccessLoss(
    httpClient: HttpClient,
    headers: Record<string, string>
  ): Promise<void> {
    try {
      const session = await firstValueFrom(
        this.postSessionRequest(
          httpClient,
          '/api/v1/session/refresh',
          null,
          headers
        )
      );
      this.writeSessionData(session);
    } catch (error) {
      console.warn('Could not refresh the organization list.', error);
    }
  }

  public getUserFromSession(): UserDto {
    const userString = localStorage.getItem('session.user');
    try {
      return JSON.parse(userString);
    } catch (error) {
      return null;
    }
  }

  public getTenantsFromSession(): TenantDto[] {
    const tenantsString = localStorage.getItem('session.tenants');
    try {
      return JSON.parse(tenantsString) ?? [];
    } catch (error) {
      return [];
    }
  }

  public getCurrentTenant(): TenantDto {
    const selectedTenantId = this.tenantService.getSelectedTenantId();
    const tenants = this.getTenantsFromSession();
    return tenants.find((t) => t.id === selectedTenantId);
  }

  /**
   * Selects the tenant and re-scopes the session to it (tenant-login) as one
   * atomic unit. This is the single entry point for changing the tenant
   * selection while only the old token is at hand: selection and token scope must
   * never diverge, otherwise tenant-scoped requests go out with a tenant-less
   * token (403 on tenant data, 500 on per-tenant settings).
   *
   * Lazy: nothing is written until the returned observable is subscribed. If the
   * re-scoping fails, the previous selection is restored (or cleared if there was
   * none) and the error is rethrown so the caller decides how to react. The one
   * exception is `TenantAccessLostError` (tenant-login 403): refreshSession has
   * then deliberately cleared the selection and informed the user, so nothing is
   * restored.
   */
  public selectTenant(tenantId: string): Observable<SessionDto> {
    return defer(() => {
      const previousTenantId = this.tenantService.getSelectedTenantId();
      this.tenantService.setSelectedTenant(tenantId);
      return this.refreshSession().pipe(
        catchError((error) => {
          // Lost access is already fully handled by refreshSession: the selection
          // was cleared on purpose. Restoring the previous one would re-select the
          // lost organization (or a different one with a tenant-less token).
          if (!(error instanceof TenantAccessLostError)) {
            this.restoreTenantSelection(previousTenantId);
          }
          return throwError(() => error);
        })
      );
    });
  }

  /**
   * Auto-select the tenant if the user has exactly one and none is selected yet.
   * See {@link selectTenant} for the atomicity and laziness guarantees.
   *
   * @returns Emits true if a tenant was auto-selected and the session re-scoped,
   * false if nothing had to be done.
   */
  public autoSelectSingleTenant(): Observable<boolean> {
    return defer(() => {
      const availableTenants = this.getTenantsFromSession();
      if (
        availableTenants?.length !== 1 ||
        this.tenantService.hasTenantSelected()
      ) {
        return of(false);
      }

      return this.selectTenant(availableTenants[0].id).pipe(map(() => true));
    });
  }

  private restoreTenantSelection(previousTenantId: string | null): void {
    if (previousTenantId) {
      this.tenantService.setSelectedTenant(previousTenantId);
    } else {
      this.tenantService.clearSelectedTenantId();
    }
  }

  public getJwtToken(): string {
    return localStorage.getItem('session.token');
  }

  public getRefreshToken(): string {
    return localStorage.getItem('session.refresh-token');
  }

  public async impersonateUser(userId: number) {
    const result = await firstValueFrom(
      this.sessionService.apiV2SessionImpersonateUserIdPost(userId)
    );
    localStorage.setItem(
      'session.impersonatedBy',
      JSON.stringify(this.getUserFromSession())
    );
    this.writeSessionData(result);
  }

  public async stopImpersonate() {
    const result = await firstValueFrom(
      this.sessionService.apiV2SessionStopImpersonatePost()
    );
    // Leave impersonation first so the restored original session is written
    // (and synced to the dev-tools snapshot) as the regular session it is.
    localStorage.removeItem('session.impersonatedBy');
    this.writeSessionData(result);
  }

  public isImpersonating(): boolean {
    return !!localStorage.getItem('session.impersonatedBy');
  }

  public async tenantLoginAdmin(
    tenantId: string,
    selectedRoles: SecurityRole[]
  ): Promise<void> {
    if (!selectedRoles || selectedRoles.length === 0) {
      throw new Error('At least one role must be selected.');
    }
    const body: TenantLoginAdminRequestDto = {
      tenantId,
      selectedRoles,
    };
    const url =
      this.apiConfigurationService.getBasePath() +
      '/api/v2/session/tenant-login-admin';
    const result = await firstValueFrom(
      this.httpClient.post<SessionDto>(url, body)
    );
    this.tenantService.setSelectedTenant(tenantId);
    this.writeSessionData(result);
  }

  public async stopTenantLoginAdmin(): Promise<void> {
    const url =
      this.apiConfigurationService.getBasePath() +
      '/api/v2/session/stop-tenant-login';
    const result = await firstValueFrom(
      this.httpClient.post<SessionDto>(url, null)
    );
    this.tenantService.clearSelectedTenantId();
    this.writeSessionData(result);
  }

  public isTenantLoginAdmin(): boolean {
    return localStorage.getItem('session.isTenantLoginAdmin') === 'true';
  }
}
